import { sortBy } from 'lodash';
import type { StaffingPageViewModel } from 'src/pages/staffing-page/staffing-page-view-model.js';
import type { EmployeeForStaffingCalendar, PeriodRowGroup, PeriodWithMinutes, YearDay } from 'src/store/selectors';
import {
  currentEmployeeAsViewModel,
  employeesShortNames,
  employeesWithTimekeepingPeriods,
  getPeopleForAccessControl,
  getOrganization,
  staffingCalendarUserDisplaySelection,
} from 'src/store/selectors';
import type { WorkSchedule } from '@trinnvis/dabih-api-client';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import {
  createPeriodGroupsWithMinutes,
  currentUserHasStaffingCalendarAccess,
  employeeGroups,
  employeesForStaffingCalendar,
  getScheduleDayData,
  getYearDays,
  holidaysForStaffingCalendar,
  leavePeriodsForYear,
  plusTimePeriodsForYear,
  sharedVacations,
  staffingCalendarAccessList,
  staffingCalendarYear,
  timeInMinutes,
} from 'src/store/selectors';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import type { StaffingCalendar } from '../../pages/staffing-page/staffing-calendar';
import type { LeavePeriodType } from '../../pages/staffing-page/leave-period-type';
import type { LeavePeriod } from '../../pages/staffing-page/leave-period';
import type { PlusTimePeriod } from '../../pages/staffing-page/plus-time-period';
import type { PeriodNotes } from '../../pages/staffing-page/period-notes';
import type { StaffingCalendarDataDay } from '../../pages/staffing-page/staffing-calendar-data-day';
import type { StaffingCalendarDataEmployee } from '../../pages/staffing-page/staffing-calendar-data-employee';
import type { StaffingCalendarDataGroup } from '../../pages/staffing-page/staffing-calendar-data-group';
import type { StartOfDayTime } from '../../pages/staffing-page/start-of-day-time';
import type { EndOfDayTime } from '../../pages/staffing-page/end-of-day-time';
import type { StaffingCalendarUserDisplaySelection } from '../../pages/staffing-page/staffing-calendar-user-display-selection';
import { workHoursDescription } from 'src/store/selectors/employees/work-hours-description';
import type { OutsideRangeEmployee } from 'src/pages/staffing-page/outside-range-employee';
import { getTimekeepingOutsideEmploymentPeriodWarning } from 'src/utilities/time-balance/timekeeping-before-employment-start-warning';

interface SuperSimplePeriod {
  start: number;
  end: number;
  id?: number;
}

/**
 * Takes a list of non-overlapping periods and subtracts the periods from the other list
 */
function subtractPeriods(mainPeriods: SuperSimplePeriod[], periodsToSubtract: SuperSimplePeriod[]) {
  function subtractPeriod(currentMainPeriods: SuperSimplePeriod[], sp: SuperSimplePeriod) {
    const newMainPeriods: SuperSimplePeriod[] = [];
    currentMainPeriods.forEach(function (mp) {
      if (sp.start < mp.end && sp.end > mp.start) {
        if (sp.start > mp.start) {
          newMainPeriods.push({
            start: mp.start,
            end: sp.start,
          });
        }
        if (sp.end < mp.end) {
          newMainPeriods.push({ start: sp.end, end: mp.end });
        }
      } else {
        newMainPeriods.push({ start: mp.start, end: mp.end });
      }
    });
    return newMainPeriods;
  }

  periodsToSubtract.forEach(function (sp) {
    mainPeriods = subtractPeriod(mainPeriods, sp);
  });
  return mainPeriods;
}

/**
 * Takes a list of periods, merges overlapping periods, returns a list of merged periods
 */
function mergePeriods(periods: SuperSimplePeriod[]) {
  const result: SuperSimplePeriod[] = [];
  const added: number[] = [];

  function addToPeriod(periodA: SuperSimplePeriod, allPeriods: SuperSimplePeriod[]) {
    const remainingPeriods = allPeriods.filter(function (period) {
      return period.id !== undefined && added.indexOf(period.id) === -1;
    });
    remainingPeriods.forEach(function (periodB) {
      if (periodA.id !== periodB.id && periodA.start <= periodB.end && periodA.end >= periodB.start) {
        if (periodB.id !== undefined) {
          added.push(periodB.id);
        }
        if (periodA.start > periodB.start) {
          periodA.start = periodB.start;
        }
        if (periodA.end < periodB.end) {
          periodA.end = periodB.end;
        }
      }
    });
    if (periodA.id !== undefined && added.indexOf(periodA.id) === -1) {
      added.push(periodA.id);
      return { start: periodA.start, end: periodA.end };
    }
  }

  periods
    .map(function (period, index) {
      period.id = index;
      return period;
    })
    .forEach(function (period, index) {
      period.id = index;
      const expandedPeriod = addToPeriod(period, periods);
      if (expandedPeriod) {
        result.push(expandedPeriod);
      }
    });
  return result;
}

function isEmployeeActiveInYear(employee: EmployeeForStaffingCalendar, year: string): boolean {
  const yearNum = Number(year);
  if (employee.employmentStartDate) {
    const startYear = Number(employee.employmentStartDate.substring(0, 4));
    if (startYear > yearNum) {
      return false;
    }
  }
  if (employee.employmentEndDate) {
    const endYear = Number(employee.employmentEndDate.substring(0, 4));
    if (endYear < yearNum) {
      return false;
    }
  }
  return true;
}

function isEmployeeEnded(employee: EmployeeForStaffingCalendar, today: string): boolean {
  if (!employee.employmentEndDate) {
    return false;
  }
  return employee.employmentEndDate <= today;
}

function filterEmployeeGroupsByYear(
  groups: EmployeeForStaffingCalendar[][],
  year: string,
): EmployeeForStaffingCalendar[][] {
  return groups.map((group) => group.filter((e) => isEmployeeActiveInYear(e, year)));
}

function computeEmployeeData(
  year: string,
  holidays: { [p: string]: string },
  employee: EmployeeForStaffingCalendar,
): StaffingCalendarDataEmployee {
  const yearStartDate = LocalDate.fromString(year + '-01-01');
  const yearEndDate = LocalDate.fromString(year + '-12-31');
  const periods = leavePeriodsForYear(employee.leavePeriods, yearStartDate, yearEndDate);
  const periodRowGroups = createPeriodGroupsWithMinutes(periods, yearStartDate);
  let plusTimePeriods: PeriodWithMinutes[] = [];
  if (employee.plusTimePeriods) {
    plusTimePeriods = plusTimePeriodsForYear(employee.plusTimePeriods, yearStartDate);
  }
  const yearDays = getYearDays(year, holidays);
  const days = dataDays(yearDays, employee, periodRowGroups, plusTimePeriods);
  return {
    uuid: employee.uuid,
    days: days,
  };
}

interface ComputedCalendarData {
  calendarData: StaffingCalendar;
  chartGroups: StaffingCalendarDataGroup[];
  endedActiveEmployees: EmployeeForStaffingCalendar[][];
}

function computeCalendarData(state: State): ComputedCalendarData {
  const year = staffingCalendarYear(state);
  const today = state.today;
  const holidays = holidaysForStaffingCalendar();
  const filteredGroups = filterEmployeeGroupsByYear(employeeGroups(state), year);

  const activeGroupsData: StaffingCalendarDataGroup[] = [{ employees: [] }, { employees: [] }, { employees: [] }];
  const endedGroupData: StaffingCalendarDataGroup = { employees: [] };
  const chartGroups: StaffingCalendarDataGroup[] = [{ employees: [] }, { employees: [] }, { employees: [] }];
  const endedActiveEmployees: EmployeeForStaffingCalendar[][] = [[], [], [], []];

  filteredGroups.forEach((group, groupIndex) => {
    group.forEach((employee) => {
      const employeeData = computeEmployeeData(year, holidays, employee);
      chartGroups[groupIndex].employees.push(employeeData);
      if (isEmployeeEnded(employee, today)) {
        endedGroupData.employees.push(employeeData);
        endedActiveEmployees[3].push(employee);
      } else {
        activeGroupsData[groupIndex].employees.push(employeeData);
        endedActiveEmployees[groupIndex].push(employee);
      }
    });
  });

  const data: StaffingCalendarDataGroup[] = [...activeGroupsData];
  if (endedGroupData.employees.length > 0) {
    data.push(endedGroupData);
  }

  return {
    calendarData: { data },
    chartGroups,
    endedActiveEmployees,
  };
}

function flattenPeriodRowGroups(rowGroups: PeriodRowGroup[]): PeriodWithMinutes[] {
  const periods: PeriodWithMinutes[] = [];
  rowGroups.forEach((group) => {
    group.rows.forEach((row) => {
      row.forEach((period) => {
        periods.push(period);
      });
    });
  });
  return periods;
}

function extractNotes(period: PeriodWithMinutes): PeriodNotes {
  let notes = period.notes ?? '';
  if (notes.length > 100) {
    notes = notes.substring(0, 100) + '…';
  }

  return {
    type: period.type as LeavePeriodType | 'plusTime',
    confirmed: period.confirmed,
    notes: notes,
  };
}

/**
 * Takes a list of days, an employee, and data for leave periods, plus time and holidays
 * Returns a list of days for that employee, each with all data needed in a graphic staffing calendar
 */
function dataDays(
  days: YearDay[],
  employee: EmployeeForStaffingCalendar,
  periodRowGroups: PeriodRowGroup[],
  plusTimePeriods: PeriodWithMinutes[],
) {
  const result: StaffingCalendarDataDay[] = [];
  days.forEach(function (day) {
    const dayDate = LocalDate.fromString(day.date);
    const workSchedule = getScheduleDayData(dayDate, employee);

    const workPeriods: { startMinutes: number; endMinutes: number }[] = [];

    if (workSchedule.workHours > 0 && workSchedule.start && workSchedule.end) {
      workPeriods.push({
        startMinutes: timeInMinutes(workSchedule.start),
        endMinutes: timeInMinutes(workSchedule.end),
      });
    }

    const periodGroupsForDay = periodRowGroups.filter(function (group) {
      return group.startMinutes < day.endMinutes && group.endMinutes > day.startMinutes;
    });
    const dataLeavePeriods: LeavePeriod[] = [];
    if (periodGroupsForDay.length) {
      periodGroupsForDay.forEach((periodGroupForDay) => {
        const groupRows = periodGroupForDay.rows;
        groupRows.forEach(function (row, index) {
          row.forEach(function (period) {
            if (period.startMinutes < day.endMinutes && period.endMinutes > day.startMinutes) {
              dataLeavePeriods.push(leavePeriod(period, day, index, groupRows.length));
            }
          });
        });
      });
    }
    const dataPlusTimePeriods: PlusTimePeriod[] = [];
    if (plusTimePeriods.length) {
      plusTimePeriods.forEach(function (period) {
        if (period.startMinutes < day.endMinutes && period.endMinutes > day.startMinutes) {
          const dataPlusTimePeriod = plusTimePeriod(period, day);
          dataPlusTimePeriods.push(dataPlusTimePeriod);
        }
      });
    }
    const leavePeriodNotes: PeriodNotes[] = flattenPeriodRowGroups(periodGroupsForDay)
      .filter((n) => n.notes !== '')
      .filter((period) => period.startMinutes >= day.startMinutes && period.startMinutes < day.endMinutes)
      .map(extractNotes);
    // plus time notes only on the first day
    const plusTimePeriodNotes: PeriodNotes[] = plusTimePeriods
      .filter((n) => n.notes !== '')
      .filter((period) => period.startMinutes >= day.startMinutes && period.startMinutes < day.endMinutes)
      .map(extractNotes);

    const periodsWithNotes = plusTimePeriodNotes.concat(leavePeriodNotes);
    result.push({
      date: day.date,
      unitStart: day.unitStart,
      workPeriods: workPeriods,
      plusTimePeriods: dataPlusTimePeriods,
      leavePeriods: dataLeavePeriods,
      periodsWithNotes: periodsWithNotes.filter((n) => n.notes !== ''),
      workSchedule: workSchedule,
    });
  });
  return result;
}

function relativeMinutes(start: number, end: number, day: YearDay): { start: number; end: number } {
  let relStartMinutes = 0;
  let relEndMinutes = 1440;
  if (start > day.startMinutes) {
    relStartMinutes = start - day.startMinutes;
  }
  if (end < day.endMinutes) {
    relEndMinutes = end - day.startMinutes;
  }
  return {
    start: relStartMinutes,
    end: relEndMinutes,
  };
}

function leavePeriod(period: PeriodWithMinutes, day: YearDay, stackIndex: number, stackCount: number): LeavePeriod {
  const result: LeavePeriod = {
    startMinutes: relativeMinutes(period.startMinutes, period.endMinutes, day).start,
    endMinutes: relativeMinutes(period.startMinutes, period.endMinutes, day).end,
    stackCount: stackCount,
    stackIndex: stackIndex,
    type: period.type as LeavePeriodType,
    confirmed: period.confirmed,
  };

  if (period.subPeriods && period.subPeriods.length) {
    result.exceptions = period.subPeriods
      .filter(function (subPeriod) {
        return subPeriod.start === day.date;
      })
      .map((subPeriod) => {
        return {
          type: subPeriod.type as LeavePeriodType,
          startMinutes: relativeMinutes(subPeriod.startMinutes, subPeriod.endMinutes, day).start,
          endMinutes: relativeMinutes(subPeriod.startMinutes, subPeriod.endMinutes, day).end,
        };
      });
  }
  return result;
}

function plusTimePeriod(period: PeriodWithMinutes, day: YearDay): PlusTimePeriod {
  let relStartMinutes = 0;
  let relEndMinutes = 1440;
  if (period.startMinutes > day.startMinutes) {
    relStartMinutes = period.startMinutes - day.startMinutes;
  }
  if (period.endMinutes < day.endMinutes) {
    relEndMinutes = period.endMinutes - day.startMinutes;
  }
  return {
    type: 'plusTime',
    confirmed: period.confirmed,
    startMinutes: relStartMinutes,
    endMinutes: relEndMinutes,
  };
}

function getSchedulesForYear(schedules: WorkSchedule[], year: string): WorkSchedule[] {
  const result: WorkSchedule[] = [];
  const yearStart = LocalDate.fromString(year + '-01-01');
  let prevSchedule: WorkSchedule | undefined = undefined;
  schedules.forEach((schedule) => {
    const scheduleStart = LocalDate.fromString(schedule.start);
    const scheduleStartYear = scheduleStart.year();
    if (scheduleStart.isAfter(yearStart) && prevSchedule) {
      result.push(prevSchedule);
    }
    if (scheduleStartYear + '' === year || (schedules.length === 1 && scheduleStart.isBefore(yearStart))) {
      result.push(schedule);
      prevSchedule = undefined;
    } else if (scheduleStart.isBefore(yearStart)) {
      prevSchedule = schedule;
    }
  });
  return result;
}

function getMaxWorkdayRangeInSchedule(schedule: WorkSchedule): { start: number; end: number } {
  let start = 1440;
  let end = 0;
  const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  if (schedule.workHoursDefinedPerDay) {
    schedule.workWeeks.forEach((week) => {
      weekdays.forEach((weekday) => {
        if (week[weekday].workDay) {
          if (timeInMinutes(week[weekday].start) < start) {
            start = timeInMinutes(week[weekday].start);
          }
          if (timeInMinutes(week[weekday].end) > end) {
            end = timeInMinutes(week[weekday].end);
          }
        }
      });
    });
  } else {
    if (schedule.workDayStartTime && timeInMinutes(schedule.workDayStartTime) < start) {
      start = timeInMinutes(schedule.workDayStartTime);
    }
    if (schedule.workDayEndTime && timeInMinutes(schedule.workDayEndTime) > end) {
      end = timeInMinutes(schedule.workDayEndTime);
    }
  }
  return { start, end };
}

function getOutsideRangeEmployees(
  employees: EmployeeForStaffingCalendar[],
  userDisplaySelection: StaffingCalendarUserDisplaySelection,
  year: string,
): OutsideRangeEmployee[] {
  const start = Number(userDisplaySelection.start.slice(0, 2)) * 60;
  const end = Number(userDisplaySelection.end.slice(0, 2)) * 60;
  const result: OutsideRangeEmployee[] = [];
  employees.forEach((employee) => {
    const scheduleDates: { date: string; dateString: string }[] = [];
    const exceptionDates: { date: string; dateString: string }[] = [];
    const employeeSchedules = sortBy(employee.workSchedules, [(item) => item.start]);
    const schedulesForYear = getSchedulesForYear(employeeSchedules, year);
    schedulesForYear.forEach((schedule) => {
      const maxWorkday = getMaxWorkdayRangeInSchedule(schedule);
      if (maxWorkday.start < start || maxWorkday.end > end) {
        scheduleDates.push({ date: schedule.start, dateString: workHoursDescription(schedule) });
      }
    });
    employee.workScheduleExceptions.forEach((exception) => {
      if (
        exception.date.slice(0, 4) === year &&
        exception.start &&
        exception.end &&
        timeInMinutes(exception.start) < timeInMinutes(exception.end) &&
        (timeInMinutes(exception.start) < start || timeInMinutes(exception.end) > end)
      ) {
        exceptionDates.push({
          date: exception.date,
          dateString:
            LocalDate.fromString(exception.date).toStringForDisplayWithDayAndMonth() +
            ' ' +
            exception.start +
            '-' +
            exception.end,
        });
      }
    });
    // Schedules and exceptions should be presented separately, even if employee name is repeated
    if (scheduleDates.length) {
      result.push({ name: employee.name, uuid: employee.uuid, schedule: true, dates: scheduleDates });
    }
    if (exceptionDates.length) {
      result.push({ name: employee.name, uuid: employee.uuid, schedule: false, dates: exceptionDates });
    }
  });
  return result;
}

function getCalendarDayRange(userDisplaySelection: StaffingCalendarUserDisplaySelection): {
  start: StartOfDayTime;
  end: EndOfDayTime;
} {
  return {
    start: userDisplaySelection.start.slice(0, 2) as StartOfDayTime,
    end: userDisplaySelection.end.slice(0, 2) as EndOfDayTime,
  };
}

export function staffingPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): StaffingPageViewModel {
  const { calendarData, chartGroups, endedActiveEmployees } = computeCalendarData(state);
  const shortNames = employeesShortNames(state);
  const organization = getOrganization(state);
  if (organization === undefined) {
    throw new Error('Illegal state (E153), organization not found');
  }

  const currentEmployee = currentEmployeeAsViewModel(state);
  const currentUserIsOwner = (currentEmployee?.email?.toUpperCase() ?? '') === organization.ownerEmail.toUpperCase();

  const accessList = staffingCalendarAccessList(state);

  const displayGroups: { items: EmployeeForStaffingCalendar[]; ended: boolean }[] = [
    { items: endedActiveEmployees[0], ended: false },
    { items: endedActiveEmployees[1], ended: false },
    { items: endedActiveEmployees[2], ended: false },
  ];
  if (endedActiveEmployees[3].length > 0) {
    displayGroups.push({ items: endedActiveEmployees[3], ended: true });
  }

  return {
    ...viewModel,
    icon: 'staffing',
    type: 'staffing-page',
    href: hrefPrefix,
    year: state.staffingCalendarYear,
    calendarData: calendarData,
    groupGraphData: groupGraphData({ data: chartGroups }),
    currentUserHasExtendedStaffingAccess: currentUserHasStaffingCalendarAccess(state),
    currentUserCanEditStaffingAccess: currentUserHasStaffingCalendarAccess(state) || currentUserIsOwner,
    leavePeriodEditRestriction: organization.leavePeriodEditRestriction,
    timekeepingBeforeEmploymentStartWarning: getTimekeepingOutsideEmploymentPeriodWarning(
      employeesWithTimekeepingPeriods(state),
    ),
    accessList: accessList,
    accessEmployees: getPeopleForAccessControl(state).map((e) => {
      const d = currentUserIsOwner && currentEmployee?.uuid === e.id ? false : e.disabled;
      return {
        value: e.id,
        text: e.value,
        disabled: d,
      };
    }),
    employeeGroups: displayGroups.map((g) => ({
      items: g.items.map((e) => ({
        uuid: e.uuid,
        displayName: shortNames.find((n) => n.uuid === e.uuid)?.name || e.name,
      })),
      ended: g.ended,
    })),
    employeesForStaffingCalendar: employeesForStaffingCalendar(state),
    employeesForShare: employeesShortNames(state)
      .filter((e) => e.name !== '')
      .map((e) => ({
        value: e.uuid,
        text: e.name,
      })),
    vacationSummaryShares: sharedVacations(state),
    vacationSummaryEmployees: organization.vacationSummaryEmployees,
    vacationSummaryNotes: organization.vacationSummaryNotes,
    today: LocalDate.fromString(state.today),
    calendarDayRange: getCalendarDayRange(staffingCalendarUserDisplaySelection(state)),
    outsideRangeEmployees: getOutsideRangeEmployees(
      employeesForStaffingCalendar(state).filter((e) => isEmployeeActiveInYear(e, state.staffingCalendarYear)),
      staffingCalendarUserDisplaySelection(state),
      state.staffingCalendarYear,
    ),
    zoom: staffingCalendarUserDisplaySelection(state).zoom,
    isMailinatorAccount: organization.ownerEmail.toLowerCase().endsWith('@mailinator.com'),
  };
}

function groupGraphData(staffingCalendar: StaffingCalendar) {
  const days: number[][] = [];
  let employeesCount = 0;
  staffingCalendar.data.forEach(function (group, groupIndex) {
    group.employees.forEach(function (employee) {
      employeesCount += 1;
      employee.days.forEach(function (day, dayIndex) {
        if (days[dayIndex] === undefined) {
          days.push([0, 0, 0]);
        }
        days[dayIndex][groupIndex] += calculateWorkDays(day);
      });
    });
  });
  return days.map((groups) => {
    return groups.map((group) => {
      return 'height: ' + (group / employeesCount) * 100 + '%';
    });
  });
}

function calculateWorkDays(day: StaffingCalendarDataDay): number {
  // Calculate total work minutes:
  // workdays minus leave periods adding plus time periods (for use in group graph)
  // Periods are calculated regardless of status (confirmed true/false)
  let totalWorkMinutes = 0;
  let totalWorkDays: number;

  let work = day.workPeriods.map((p) => {
    return { start: p.startMinutes, end: p.endMinutes };
  });

  let leave = day.leavePeriods.map((period) => {
    return { start: period.startMinutes, end: period.endMinutes };
  });
  const workPeriodsInLeave: SuperSimplePeriod[] = [];
  day.leavePeriods.forEach((period) => {
    if (period.exceptions && period.exceptions.length) {
      const workPeriodInLeave = {
        start: period.exceptions[0].startMinutes,
        end: period.exceptions[0].endMinutes,
      };
      workPeriodsInLeave.push(workPeriodInLeave);
    }
  });
  if (workPeriodsInLeave.length) {
    leave = subtractPeriods(leave, workPeriodsInLeave);
  }

  const plus = day.plusTimePeriods.map((period) => {
    return { start: period.startMinutes, end: period.endMinutes };
  });

  if (leave) {
    work = subtractPeriods(work, leave);
  }
  if (plus) {
    work = mergePeriods(work.concat(plus));
  }
  work.forEach(function (item) {
    totalWorkMinutes += item.end - item.start;
  });

  totalWorkDays = totalWorkMinutes / 450; // = 7.5 hours
  if (totalWorkDays > 1) {
    totalWorkDays = 1;
  }

  return totalWorkDays;
}
